/**
 * ProtectedRoute — wraps any route that requires authentication.
 * Redirects unauthenticated users to / (login).
 * ADR-020, E32 T-3902.
 */

'use client'
import { useEffect, useState } from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import { getSession, type SessionUser } from '@/lib/auth'

interface Props {
  requiredRole?: SessionUser['role'][]
}

export function ProtectedRoute({ requiredRole }: Props) {
  const [status, setStatus] = useState<'loading' | 'ok' | 'unauth' | 'forbidden'>('loading')

  useEffect(() => {
    getSession().then((session) => {
      if (!session) {
        setStatus('unauth')
        return
      }
      if (requiredRole && !requiredRole.includes(session.user.role)) {
        setStatus('forbidden')
        return
      }
      setStatus('ok')
    })
  }, [requiredRole])

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-300 border-t-[#0EA5E9]" />
      </div>
    )
  }

  if (status === 'unauth') return <Navigate to="/" replace />
  if (status === 'forbidden') return <Navigate to="/dashboard" replace />

  return <Outlet />
}
